// One-time client bootstrap that purges the ~280MB transformers.js model
// cache and any other on-device AI caches left over from before Task #103
// removed the on-device AI runtime. Runs at most once per browser via a
// localStorage marker.

const PURGE_FLAG = "onDeviceAI.cache.purged.v1";
const LEGACY_LOCALSTORAGE_KEYS = [
  "onDeviceAI.optedIn.v1",
  "onDeviceAI.modelReady.v1",
  "onDeviceAI.capability.v3",
  "onDeviceAI.capability.v2",
  "onDeviceAI.capability.v1",
];

// Cache Storage names that the previous runtime + transformers.js wrote
// to. The huggingface/transformers cache name varies a bit by version, so
// we sweep anything that looks like it belonged to it.
function isLegacyOnDeviceCacheName(name: string): boolean {
  return (
    name === "transformers-cache" ||
    name.startsWith("transformers-cache-") ||
    name === "onnxruntime-web" ||
    name.startsWith("hf-") ||
    name.includes("Xenova")
  );
}

export function purgeOnDeviceAICache(): void {
  try {
    if (localStorage.getItem(PURGE_FLAG) === "1") return;
  } catch {
    // Best-effort; if storage is unavailable we still try the purge but
    // skip the marker so we may try again next visit.
  }

  // Best-effort: fire-and-forget. We never block app startup on this.
  void (async () => {
    let cacheSweepOk = false;
    try {
      if (typeof caches !== "undefined" && caches?.keys) {
        const names = await caches.keys();
        const targets = names.filter((n) => isLegacyOnDeviceCacheName(n));
        if (targets.length === 0) {
          cacheSweepOk = true;
        } else {
          const results = await Promise.all(
            targets.map((n) => caches.delete(n).catch(() => false)),
          );
          // Only consider the sweep successful if every targeted cache was
          // actually deleted. A transient failure must NOT mark the purge
          // complete, otherwise the ~280MB stays on disk forever.
          cacheSweepOk = results.every((r) => r === true);
        }
      } else {
        // Cache Storage not available in this environment — nothing to do.
        cacheSweepOk = true;
      }
    } catch {
      cacheSweepOk = false;
    }

    // Legacy localStorage keys are cheap to clear and safe to retry; always
    // attempt them, but their result does not gate the persistence marker.
    try {
      for (const k of LEGACY_LOCALSTORAGE_KEYS) {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      }
    } catch {
      /* ignore */
    }

    if (cacheSweepOk) {
      try {
        localStorage.setItem(PURGE_FLAG, "1");
      } catch {
        /* ignore */
      }
    }
  })();
}
